import React,{useState,useEffect} from 'react';
import {Navigate,useNavigate} from 'react-router-dom';
import {HashLink as Link} from 'react-router-hash-link';
import {Helmet} from 'react-helmet';
import {LockClosedIcon,MailIcon,ShieldCheckIcon,AcademicCapIcon} from '@heroicons/react/outline';
import {enqueueSnackbar} from 'notistack';
import globalSettingsManagerUtil from '../../utils/global-settings-manager-util';
import submitFormUtil from '../../utils/submit-form-util';
import locales from '../../locales/locales';
import Navbar from '../navbar/navbar';
import EmailVerifier from '../email-verifier/email-verifier';
import ResetPassword from '../reset-password/reset-password';
import Footer from '../footer/footer';
const Auth=({isLogin,globalSettings})=>{
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [confirmPassword,setConfirmPassword]=useState('');
  const [marketingOptIn,setMarketingOptIn]=useState(true);
  const [areTermsAccepted,setAreTermsAccepted]=useState(false);
  const [code,setCode]=useState(null);
  const [emailError,setEmailError]=useState(null);
  const [passwordError,setPasswordError]=useState(null);
  const [confirmPasswordError,setConfirmPasswordError]=useState(null);
  const [termsError,setTermsError]=useState(null);
  const [isEmailVerifierVisible,setIsEmailVerifierVisible]=useState(false);
  const [isResetPasswordVisible,setIsResetPasswordVisible]=useState(false);
  const [isLoading,setIsLoading]=useState(false);
  const navigate=useNavigate();
  useEffect(()=>{
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setMarketingOptIn(true);
    setAreTermsAccepted(false);
    setCode(null);
  },[isLogin]);
  useEffect(()=>{
    if(emailError){
      setEmailError(null);
    }
  },[email,isLogin]);
  useEffect(()=>{
    if(passwordError){
      setPasswordError(null);
    }
  },[password,isLogin]);
  const checkConfirmPassword=()=>{
    const ok=confirmPassword===password;
    if(!ok){
      setConfirmPasswordError(locales.authPasswordsDoNotMatch);
    }
    else if(confirmPasswordError){
      setConfirmPasswordError(null);
    }
    return ok;
  }
  useEffect(()=>{
    if(!isLogin&&confirmPassword){
      checkConfirmPassword();
    }
  },[password,confirmPassword,isLogin]);
  useEffect(()=>{
    if(termsError){
      setTermsError(null);
    }
  },[areTermsAccepted]);
  const handleSubmit=async()=>{
    if(isLoading){
      return;
    }
    setIsLoading(true);
    if(isLogin){
      const res=await submitFormUtil('/login',{
        'email':[email,setEmailError],
        'password':[password,setPasswordError],
      },{
        'no_such_email':{
          'email':locales.authNoSuchEmail,
        },
        'password_incorrect':{
          'password':locales.authPasswordIncorrect,
        },
      });
      if(res){
        globalSettingsManagerUtil.updateGlobalSettings({
          token:res.token,
        });
        enqueueSnackbar(locales.authWelcomeBack[globalSettings.lang],{variant:'success'});
        navigate(`/${globalSettings.lang}/translator`,{replace:true});
      }
    }
    else if(!areTermsAccepted){
      setTermsError(locales.authTermsNotAccepted);
    }
    else if(checkConfirmPassword()){
      const res=await submitFormUtil('/register',{
        'email':[email,setEmailError],
        'password':[password,setPasswordError],
        'marketing_opt_in':[marketingOptIn,()=>{}],
        'code':[code||'000000',()=>{}],
      },{
        'email_taken':{
          email:locales.authEmailTaken,
        },
        'register_disabled':(()=>{
          setTermsError(locales.authRegisterDisabled);
        }),
        'code_invalid':(()=>{setIsEmailVerifierVisible(true);}),
      });
      if(res){
        enqueueSnackbar(locales.authAccountCreated[globalSettings.lang],{variant:'success'});
        navigate(`/${globalSettings.lang}/login`,{replace:true});
      }
    }
    setIsLoading(false);
  };
  useEffect(()=>{
    if(!code){
      return;
    }
    if(isLogin){
      setIsResetPasswordVisible(true);
    }
    else{
      handleSubmit();
    }
  },[code]);
  const handleEmailVerifierExit=(status)=>{
    if(status){
      setEmail(status[1]);
      setCode(status[0]);
    }
    setIsEmailVerifierVisible(false);
  };
  const handleResetPasswordExit=(success)=>{
    if(!success){
      setIsEmailVerifierVisible(true);
    }
    setIsResetPasswordVisible(false);
  };
  if(globalSettings.token){
    return <Navigate to={`/${globalSettings.lang}/translator`} replace={true}/>;
  }
  return (
    <div className='min-h-screen flex flex-col bg-white'>
      <Helmet>
        <title>NekoTranslate - {isLogin?locales.authSignInPageTitle[globalSettings.lang]:locales.authCreateAccountPageTitle[globalSettings.lang]}</title>
      </Helmet>
      <Navbar 
        globalSettings={globalSettings}
      />
      <div className='flex-1 bg-purple-50 pt-16'>
        <div className='w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-12 items-center'>
            <div className='order-1 md:order-1'>
              <h1 className='text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight text-center md:text-left'>
                {(isLogin?locales.authSignInHeadingPrefix:locales.authCreateAccountHeadingPrefix)[globalSettings.lang]}
                <span className='text-purple-800'>
                  {(isLogin?locales.authSignInHeadingHighlight:locales.authCreateAccountHeadingHighlight)[globalSettings.lang]}
                </span>
                {(isLogin?locales.authSignInHeadingSuffix:locales.authCreateAccountHeadingSuffix)[globalSettings.lang]}
              </h1>
              <p className='mt-4 text-xl text-gray-600 text-center md:text-left'>
                {isLogin ? locales.authSignInSubtext[globalSettings.lang] : locales.authCreateAccountSubtext[globalSettings.lang]}
              </p>
              <div className='hidden md:block mt-8 space-y-4'>
                <div className='bg-white p-6 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition'>
                  <div className='h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4'>
                    <AcademicCapIcon className='h-6 w-6 text-purple-800' />
                  </div>
                  <h3 className='text-xl font-semibold text-gray-900 mb-2'>{locales.authFlagshipLLMSupport[globalSettings.lang]}</h3>
                  <p className='text-gray-600'>
                    {locales.authFlagshipLLMSupportDescription[globalSettings.lang]}
                  </p>
                </div>
                <div className='bg-white p-6 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition'>
                  <div className='h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4'>
                    <ShieldCheckIcon className='h-6 w-6 text-purple-800' />
                  </div>
                  <h3 className='text-xl font-semibold text-gray-900 mb-2'>{locales.authPrivacyFirst[globalSettings.lang]}</h3>
                  <p className='text-gray-600'>
                    {locales.authPrivacyFirstDescription[globalSettings.lang]}
                  </p>
                </div>
              </div>
            </div>
            <div className='flex justify-center order-2 md:order-2'>
              <form
                onSubmit={(e)=>{e.preventDefault();handleSubmit();}}
                className='bg-white p-6 rounded-xl shadow-lg border border-gray-200 max-w-md w-full'
              >
                <h2 className='text-2xl font-bold text-center text-gray-900 mb-8'>
                  {isLogin ? locales.authSignInHeading[globalSettings.lang] : locales.authCreateAccountHeading[globalSettings.lang]}
                </h2>
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{locales.authEmailLabel[globalSettings.lang]}</label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <MailIcon className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      type='email'
                      placeholder='your.email@example.com'
                      value={email}
                      onChange={(e)=>{setEmail(e.target.value);}}
                      className={`w-full pl-10 pr-3 py-2 border ${emailError ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600`}
                    />
                  </div>
                  {emailError&&<p className='mt-1 text-sm text-red-600'>{emailError[globalSettings.lang]}</p>}
                </div>
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>{locales.authPasswordLabel[globalSettings.lang]}</label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <LockClosedIcon className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      type='password'
                      placeholder={isLogin ? locales.authPasswordPlaceholderLogin[globalSettings.lang] : locales.authPasswordPlaceholderRegister[globalSettings.lang]}
                      value={password}
                      onChange={(e)=>{setPassword(e.target.value);}}
                      className={`w-full pl-10 pr-3 py-2 border ${passwordError ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600`}
                    />
                  </div>
                  {passwordError&&<p className='mt-1 text-sm text-red-600'>{passwordError[globalSettings.lang]}</p>}
                </div>
                {!isLogin && (
                  <div className='mb-6'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>{locales.authConfirmPasswordLabel[globalSettings.lang]}</label>
                    <div className='relative'>
                      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                        <LockClosedIcon className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        type='password'
                        placeholder={locales.authConfirmPasswordPlaceholder[globalSettings.lang]}
                        value={confirmPassword}
                        onChange={(e)=>{setConfirmPassword(e.target.value);}}
                        className={`w-full pl-10 pr-3 py-2 border ${confirmPasswordError ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600`}
                      />
                    </div>
                    {confirmPasswordError&&<p className='mt-1 text-sm text-red-600'>{confirmPasswordError[globalSettings.lang]}</p>}
                  </div>
                )}
                {!isLogin && (
                  <div className='mb-4'>
                    <div className='flex items-start'>
                      <div className='flex items-center h-5'>
                        <input
                          type='checkbox'
                          checked={marketingOptIn}
                          onChange={(e)=>{setMarketingOptIn(e.target.checked);}}
                          className='h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500'
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <p className='text-gray-600'>
                          {locales.authMarketingOptIn[globalSettings.lang]}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {!isLogin && (
                  <div className='mb-6'>
                    <div className='flex items-start'>
                      <div className='flex items-center h-5'>
                        <input
                          type='checkbox'
                          checked={areTermsAccepted}
                          onChange={(e)=>{setAreTermsAccepted(e.target.checked);}}
                          className={`h-4 w-4 text-purple-600 ${termsError ? 'border-red-300' : 'border-gray-300'} rounded focus:ring-purple-500`}
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <p className='text-gray-600'>
                          {locales.authAgreeToThe[globalSettings.lang]}
                          <Link to={`/${globalSettings.lang}/terms-of-service`} className='text-purple-700 hover:text-purple-800 font-medium'>
                            {locales.authTermsOfService[globalSettings.lang]}
                          </Link>
                          {locales.authAnd[globalSettings.lang]}
                          <Link to={`/${globalSettings.lang}/privacy-policy`} className='text-purple-700 hover:text-purple-800 font-medium'>
                            {locales.authPrivacyPolicy[globalSettings.lang]}
                          </Link>
                          {locales.authAgreeSuffix[globalSettings.lang]}
                        </p>
                      </div>
                    </div>
                    {termsError&&<p className='mt-1 text-sm text-red-600'>{termsError[globalSettings.lang]}</p>}
                  </div>
                )}
                {isLogin ? (
                  <div className='flex justify-between items-center mb-6'>
                    <div className='flex items-center'>
                      <input
                        type='checkbox'
                        className='h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500'
                      />
                      <label className='ml-2 block text-sm text-gray-700'>
                        {locales.authRememberMe[globalSettings.lang]}
                      </label>
                    </div>
                    <button 
                      type='button'
                      onClick={()=>{setIsEmailVerifierVisible(true);}}
                      className='text-sm font-medium text-purple-700 hover:text-purple-800'
                    >
                      {locales.authForgotPassword[globalSettings.lang]}
                    </button>
                  </div>
                ) : null}
                <button
                  type='submit'
                  disabled={isLoading}
                  className={`w-full bg-purple-800 hover:bg-purple-600 text-white px-4 py-3 rounded-lg text-lg font-semibold transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? 
                    locales.authProcessing[globalSettings.lang] : 
                    (isLogin ? locales.authSignInButton[globalSettings.lang] : locales.authCreateAccountButton[globalSettings.lang])
                  }
                </button>
                <div className='mt-6 text-center'>
                  <p className='text-gray-600'>
                    {isLogin ? locales.authDontHaveAccount[globalSettings.lang] : locales.authAlreadyHaveAccount[globalSettings.lang]}
                    <Link to={isLogin?`/${globalSettings.lang}/register`:`/${globalSettings.lang}/login`} className='text-purple-700 hover:text-purple-800 font-medium'>
                      {isLogin ? locales.authSignUpLink[globalSettings.lang] : locales.authSignInLink[globalSettings.lang]}
                    </Link>
                  </p>
                </div>
              </form>
            </div>
            <div className='order-3 md:hidden'>
              <div className='mt-8 space-y-4'>
                <div className='bg-white p-6 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition'>
                  <div className='h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4'>
                    <AcademicCapIcon className='h-6 w-6 text-purple-800' />
                  </div>
                  <h3 className='text-xl font-semibold text-gray-900 mb-2'>{locales.authFlagshipLLMSupport[globalSettings.lang]}</h3>
                  <p className='text-gray-600'>
                    {locales.authFlagshipLLMSupportDescription[globalSettings.lang]}
                  </p>
                </div>
                <div className='bg-white p-6 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition'>
                  <div className='h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4'>
                    <ShieldCheckIcon className='h-6 w-6 text-purple-800' />
                  </div>
                  <h3 className='text-xl font-semibold text-gray-900 mb-2'>{locales.authPrivacyFirst[globalSettings.lang]}</h3>
                  <p className='text-gray-600'>
                    {locales.authPrivacyFirstDescription[globalSettings.lang]}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isEmailVerifierVisible&&(
        <EmailVerifier
          defaultEmail={isLogin?undefined:email}
          shallCheckForEmailExistence={isLogin}
          onExit={handleEmailVerifierExit}
          globalSettings={globalSettings}
        />
      )}
      {isResetPasswordVisible&&(
        <ResetPassword
          email={email}
          code={code}
          onExit={handleResetPasswordExit}
          globalSettings={globalSettings}
        />
      )}
      <Footer 
        globalSettings={globalSettings}
      />
    </div>
  );
};
export default Auth;

